import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, switchMap, tap } from 'rxjs';
import { UnitService } from 'shared/domain';
import {
  ConfirmationDialogItem,
  ConfirmationDialogNewComponent,
  DialogService,
  FormFieldImports,
  LabelDirective,
  SelectImports,
} from 'shared/ui-common';
import { tapAllResponses } from 'shared/util-common';

import { UnitDto } from '@treetop/shared/model';

@Component({
  standalone: true,
  imports: [TranslocoModule, MatSelectModule, MatProgressSpinnerModule, FormsModule, FormFieldImports, SelectImports, LabelDirective],
  selector: 'guideline-sidebar-unit-molar',
  templateUrl: './unit-molar.component.html',
  styleUrls: ['./unit-molar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitMolarComponent implements OnChanges {
  public unitService = inject(UnitService);
  public ref = inject(ChangeDetectorRef);
  private dialogService = inject(DialogService);

  @Input() public disableUnit: boolean;
  @Input() public molarMass: number | undefined;
  @Input() public unit: UnitDto;
  @Input() public confirmationOnUnitChange = false;

  @Output() public unitChanged = new EventEmitter<UnitDto>();

  protected availableUnits = signal<UnitDto[]>([]);
  protected seletedUnit = signal<UnitDto | undefined>(undefined);

  private setAvailableUnits = rxMethod<UnitDto>((unit$) =>
    unit$.pipe(
      tap((unit) => {
        this.availableUnits.set([unit]);
      }),
      switchMap((unit) =>
        this.unitService.getCompatibleUnits(unit?.abbreviation, !!this.molarMass).pipe(
          map((result) => ({
            result,
            unit,
          })),
        ),
      ),
      tapAllResponses(
        ({ result, unit }: { result: UnitDto[]; unit: UnitDto }) => {
          const convertibleUnits = result.filter((el) => el.name !== unit.name).map((el) => el);
          if (convertibleUnits && convertibleUnits.length > 0) {
            this.availableUnits.set([...this.availableUnits(), ...convertibleUnits]);
          }
        },
        (error) => {
          console.error(error);
        },
      ),
    ),
  );

  public ngOnChanges({ molarMass, unit }: SimpleChanges): void {
    if (!molarMass && !unit) {
      return;
    }

    this.setAvailableUnits(this.unit);
    this.seletedUnit.set(this.unit);
  }

  protected onMolarUnitSelect(selectedMolarUnitId: string) {
    const selectedMolarUnit = this.availableUnits().find((unit) => unit.id === selectedMolarUnitId);

    if (this.confirmationOnUnitChange) {
      this.dialogService
        .open<ConfirmationDialogItem>(ConfirmationDialogNewComponent, {
          width: 400,
          title: 'FlowchartChangeUnitTypeTitle',
          data: {
            text: 'FlowchartChangeUnitTypeDescription',
            confirmButton: 'Accept',
          },
        })
        .afterClosed()
        .pipe()
        .subscribe((confirmed) => {
          if (!confirmed) {
            this.seletedUnit.set(this.unit);
            return;
          }

          this.unitChanged.emit(selectedMolarUnit);
        });
    } else {
      this.unitChanged.emit(selectedMolarUnit);
    }
  }
}
